import { z } from "zod";

import { emailSchema } from "./auth";
import { MEMBERSHIP_STATUSES } from "./membership";

/**
 * Enterprise organizasyon türleri (CLOS-71 `[023]`).
 *
 * `enterprise_company` şirket (CLOS-71 `[023]`), `enterprise_law_firm` hukuk bürosu
 * (CLOS-72 `[024]`).
 *
 * İkisi ALTERNATİF DEĞİL, ayrı ve değişmez türler. Hukuk bürosu müvekkil hizmet
 * ilişkilerinin bağlamıdır; şirket değildir. Tür sonradan çevrilemiyor — veritabanında
 * kolon yazma yetkisi geri alınmış durumda.
 */
export const ENTERPRISE_TENANT_TYPES = ["enterprise_company", "enterprise_law_firm"] as const;
export type EnterpriseTenantType = (typeof ENTERPRISE_TENANT_TYPES)[number];

/**
 * Bütün çalışma alanı türleri — kişisel alan dahil (CLOS-70 `[022]`).
 *
 * `ENTERPRISE_TENANT_TYPES` "kullanıcı hangi türde organizasyon açabilir" sorusunun cevabı;
 * bu liste ise "sistemde hangi çalışma alanı türleri var" sorusunun. Kişisel alan
 * açılmıyor, hesapla birlikte DOĞUYOR — bu yüzden oluşturma şemasında yeri yok.
 */
/**
 * ⚠ `client_workspace` LİSTEDE ama `ENTERPRISE_TENANT_TYPES`'ta DEĞİL (CLOS-175 `[122]`).
 * Sponsorlu Müvekkil Alanı bir kullanıcının "açtığı" organizasyon değil; hukuk bürosunun
 * lisans hakkı altında MÜVEKKİL İÇİN kurulan ayrı bir alan. Oluşturma şemasına girseydi,
 * herkes kendine bir müvekkil alanı açabilir ve sponsorluk kavramı anlamsızlaşırdı.
 */
export const TENANT_TYPES = ["individual", ...ENTERPRISE_TENANT_TYPES, "client_workspace"] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

/**
 * KİŞİSEL ÇALIŞMA ALANI KULLANIM ARKETİPİ (CLOS-807).
 *
 * ⚠ Bu bir HESAP alanı DEĞİL, çalışma alanı alanı. Aynı gerçek kişi kişisel alanında
 * bağımsız avukat, başka bir şirkette çalışan ve üçüncü bir sözleşmede haricî katılımcı
 * olabilir. Hesaba global bir "avukattır" bayrağı konsaydı, o kişi girdiği HER alanda
 * avukat sayılırdı — remediation raporunun açıkça yasakladığı çözüm.
 *
 * `self` kendi sözleşmelerini yöneten kişi; `independent_lawyer` müvekkil adına çalışan
 * bağımsız avukat. Yalnız `individual` türünde anlamlı — kurumsal alanda türü
 * `organizationType` belirler ve iki kavram birbirinin yerine geçmez.
 *
 * ⚠ Varsayılan `self` ve bu bilinçli: kart *"default seçimi kullanıcı davranışını
 * sessizce genişletmemeli"* diyor. Mevcut kişisel alanların hepsi `self` sayılıyor —
 * kimse istemeden müvekkil yüzeyi açılmıyor.
 */
export const USAGE_ARCHETYPES = ["self", "independent_lawyer"] as const;
export type UsageArchetype = (typeof USAGE_ARCHETYPES)[number];

/** Kullanıcıya görünen adlar — `docs/glossary.md` ile birebir (kriter 20). */
export const USAGE_ARCHETYPE_NAMES: Record<UsageArchetype, string> = {
  self: "Kendi sözleşmelerimi yönetiyorum",
  independent_lawyer: "Müvekkiller adına çalışan bağımsız avukatım",
};

/**
 * Arketip değiştirme gövdesi (CLOS-807).
 *
 * Tek alan ama yine bir NESNE: kart geçişin *"açık, kontrollü command"* olmasını istiyor
 * ve ileride gerekçe/onay alanı eklendiğinde çağıranların sözleşmesi değişmesin.
 */
export const updateUsageArchetypeSchema = z.object({
  usageArchetype: z.enum(USAGE_ARCHETYPES, { error: "Kullanım biçimi seçilmelidir" }),
});
export type UpdateUsageArchetypeInput = z.infer<typeof updateUsageArchetypeSchema>;

/**
 * ORGANİZASYON TÜRÜ DÜZELTMESİ (CLOS-812).
 *
 * ⚠ Bu bir PROFİL ALANI DEĞİL. Kart açıkça yasaklıyor: *"Organization Profile içinde
 * sıradan COMPANY <-> LAW_FIRM dropdown yok"*. Tür oluşturma anında kesinleşir; buradaki
 * akış bir DÜZELTMEDİR — yanlış türle açılmış, henüz kullanılmamış bir organizasyonu
 * doğru türe taşımak için.
 *
 * ⚠ GEREKÇE ZORUNLU ve kısa metin kabul edilmiyor. Düzeltme geri alınamaz bir kararı
 * bir kez daha değiştiriyor; audit satırında "neden" yoksa altı ay sonra kimse bu
 * organizasyonun neden tür değiştirdiğini bilemez. Sınır 10 karakter: "hata" yazıp
 * geçmeyi engelliyor, kullanıcıyı kompozisyona da zorlamıyor.
 */
export const correctTenantTypeSchema = z.object({
  type: z.enum(ENTERPRISE_TENANT_TYPES, { error: "Düzeltilecek tür seçilmelidir" }),
  reason: z
    .string({ error: "Düzeltme gerekçesi zorunludur" })
    .trim()
    .min(10, "Düzeltme gerekçesi en az 10 karakter olmalıdır")
    .max(500, "Düzeltme gerekçesi en fazla 500 karakter olabilir"),
});
export type CorrectTenantTypeInput = z.infer<typeof correctTenantTypeSchema>;

/**
 * DÜZELTME ÖN KONTROLÜ (preflight) — kart: *"etkilenen domainler, migration riski ve
 * rollback sonucu gösterilir"*.
 *
 * ⚠ Sayılar EKRANA çıkmak için var. "Düzeltilemez" demek yetmiyor; kullanıcı NEDEN
 * düzeltilemediğini görmeli, yoksa arıza "sistem çalışmıyor" gibi okunur. Her satır bir
 * domain ve o domaindeki kayıt sayısı.
 */
export const typeCorrectionBlockerSchema = z.object({
  domain: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type TypeCorrectionBlocker = z.infer<typeof typeCorrectionBlockerSchema>;

export const typeCorrectionPreflightSchema = z.object({
  tenantId: z.string(),
  currentType: z.enum(ENTERPRISE_TENANT_TYPES),
  /** Kullanılmamış kabuk mu? `false` ise düzeltme REDDEDİLİR — sessiz dönüşüm yok. */
  correctable: z.boolean(),
  blockers: z.array(typeCorrectionBlockerSchema),
});
export type TypeCorrectionPreflight = z.infer<typeof typeCorrectionPreflightSchema>;

/**
 * Türlerin kullanıcıya görünen adları (CLOS-79 `[042]` — *"alan türleri ayırt edilir"*).
 *
 * `docs/glossary.md` ile birebir. Arayüz kendi etiketini üretmez: sözlükte olmayan bir
 * terimle kod yazılmaz (kriter 20) ve iki yerde tutulan bir eşleme er ya da geç ayrışır.
 */
export const TENANT_TYPE_NAMES: Record<TenantType, string> = {
  individual: "Kişisel çalışma alanı",
  enterprise_company: "Şirket",
  enterprise_law_firm: "Hukuk bürosu",
  client_workspace: "Müvekkil Alanı",
};

/**
 * Kullanıcının erişebildiği çalışma alanlarının listesi — `GET /api/tenants/mine`.
 *
 * ### Neden şema var
 *
 * Bu yanıtın şeması yoktu; arayüz elle bir `interface` tutuyordu ve sunucunun döndürdüğü
 * `type` alanını **sessizce düşürüyordu**. Sonucu ölçüldü: kişisel çalışma alanı, ekranda
 * organizasyonlardan ayırt edilemeden "Firma Seç" listesinde kullanıcının kendi adıyla
 * görünüyordu. Tek sözleşme, iki tarafın aynı alanları görmesini yapısal olarak sağlıyor.
 *
 * ### `selectable` neden sunucudan geliyor
 *
 * Seçilebilirlik kararı `grantsWorkspaceAccess`'ten türer ve `requireTenant` kapısı da
 * aynı fonksiyonu kullanır. İstemci `status === "active"` diye kendi kararını üretseydi,
 * kural iki yerde yazılmış olur ve kapı ile liste bir gün ayrışırdı — kullanıcı seçebildiği
 * ama giremediği bir alan görürdü.
 */
export const myWorkspaceSchema = z.object({
  tenantId: z.uuid(),
  name: z.string(),
  type: z.enum(TENANT_TYPES),
  /**
   * Kanonik rol ÖRNEĞİNİN adı; kişisel çalışma alanında `null` (sistem rolü üretmiyor).
   * Metinsel `memberships.role` kademesi DEĞİL: özel roller (CLOS-85 `[028]`) o kümede
   * yok ve arayüzdeki sabit eşleme onlarda boş etiket basardı.
   */
  roleName: z.string().nullable(),
  status: z.enum(MEMBERSHIP_STATUSES),
  /** Askı/sonlandırma gerekçe KODU; kategori `config/membership.json`'dan gelir. */
  statusReason: z.string().nullable(),
  selectable: z.boolean(),
  /**
   * MÜVEKKİL ALANI için hizmet veren HUKUK BÜROSUNUN adı (CLOS-1123, Halit'in merge koşulu).
   * `sponsored_client_workspaces.sponsor_tenant_id → tenants.name` projeksiyonu; alan ADINDAN
   * ayrıştırılmaz. Alan adı kullanıcı tarafından değiştirilebilir ("Eren San Hukuk İşleri"),
   * ama hangi büroyla ilişkili olduğu domain bilgisidir ve seçicide kaybolmamalı. Diğer
   * türlerde `null`; eski istemciler için alan isteğe bağlı.
   */
  sponsorName: z.string().nullable().optional(),
  /**
   * Kullanım arketipi — YALNIZ kişisel alanda dolu, diğerlerinde `null` (CLOS-807).
   *
   * Listede taşınıyor çünkü alan seçim ekranı ve seçici kişisel alanı diğerlerinden
   * ayırt ederken bunu gösterebilmeli; istemci `type === "individual"` görüp arketipi
   * ayrıca sormak zorunda kalmasın.
   */
  usageArchetype: z.enum(USAGE_ARCHETYPES).nullable(),
});
export type MyWorkspace = z.infer<typeof myWorkspaceSchema>;

/**
 * Aktif çalışma alanının BAĞLAMI — `GET /api/tenants/:id/context` (CLOS-80 `[043]`).
 *
 * ### Ne işe yarıyor
 *
 * Bağlam değiştirme akışının sunucu ayağı. İstemci hedef alana geçmeden önce buradan
 * "gerçekten girebiliyor muyum ve orada neyim var" cevabını alıyor. Doğrulanabilir bir
 * uç olmadan kartın *"geçiş sırasında hata güvenli geri dönüş sağlar"* kriteri anlamsız
 * kalırdı: hiç denenmeyen bir geçiş hiç başarısız olamaz.
 *
 * ### `permissions` neden burada
 *
 * Kart açık: *"Permission kararı CLOS-84'ün ortak sunucu tarafı kontrol mekanizmasından
 * tüketilir; bu kart içinde ayrı bir permission altyapısı kurulmaz."* Bu alan
 * `listUserPermissions`'ın çıktısını taşıyor — yeni bir hesap değil, mevcut motorun
 * projeksiyonu.
 *
 * ⚠ **Bu liste bir GÜVENLİK SINIRI DEĞİL, bir kolaylıktır.** Menü ve düğme görünürlüğü
 * için var. Gerçek kapı sunucudaki `requirePermission`'dır; istemcinin bu diziyi
 * değiştirmesi hiçbir kapıyı açmaz.
 */
export const workspaceContextSchema = z.object({
  tenantId: z.uuid(),
  name: z.string(),
  type: z.enum(TENANT_TYPES),
  /** Kişisel çalışma alanında `null`: orada rol örneği yok (`[025]`). */
  roleName: z.string().nullable(),
  /** Kişisel çalışma alanında BOŞ dizi — yetkisizlik değil, rolsüzlük. */
  permissions: z.array(z.string()),
  /**
   * Kullanım arketipi — yalnız kişisel alanda dolu (CLOS-807).
   *
   * ⚠ Bağlam DTO'sunda olması ŞART: yetenek hesabı (hangi domain görünür) sunucunun
   * kararı ve istemci onu buradan okuyor. İstemci kendi başına türetseydi, kural iki
   * yerde yazılmış olur ve menü ile sunucu bir gün ayrışırdı.
   */
  usageArchetype: z.enum(USAGE_ARCHETYPES).nullable(),
});
export type WorkspaceContext = z.infer<typeof workspaceContextSchema>;

/**
 * Organizasyonun güvenlik politikası (CLOS-292 `[046]`).
 *
 * Bugün tek madde var: 2FA zorunluluğu. Şema yine de bir NESNE — politika büyüdüğünde
 * (parola kuralı, oturum ömrü) çağıranların sözleşmesi değişmesin diye.
 */
export const securityPolicySchema = z.object({
  twoFactorRequired: z.boolean(),
});
export type SecurityPolicy = z.infer<typeof securityPolicySchema>;

export const updateSecurityPolicySchema = z.object({
  twoFactorRequired: z.boolean({ error: "İki aşamalı doğrulama tercihi belirtilmelidir" }),
});
export type UpdateSecurityPolicyInput = z.infer<typeof updateSecurityPolicySchema>;

export const createTenantSchema = z.object({
  name: z
    .string({ error: "Organizasyon adı zorunludur" })
    .trim()
    .min(2, "Organizasyon adı en az 2 karakter olmalıdır")
    .max(200, "Organizasyon adı en fazla 200 karakter olabilir"),

  /**
   * Tür AÇIKÇA gönderilir; varsayılanı YOK.
   *
   * Varsayılan koysaydık kullanıcı yanlış türde bir organizasyon açabilir ve tür
   * değiştirilemez olduğu için (DB'de kolon yetkisi geri alınmış) geri dönemezdi.
   * Kritik ve geri alınamaz bir seçimde sessiz varsayılan, kullanıcıyı tuzağa düşürür.
   */
  type: z.enum(ENTERPRISE_TENANT_TYPES, { error: "Organizasyon türü seçilmelidir" }),
});
export type CreateTenantInput = z.infer<typeof createTenantSchema>;

/**
 * Organizasyon daveti (CLOS-73 `[034]`).
 *
 * `[034]` öncesinde bu şema `role: "admin" | "member"` alıyordu ve uç mevcut bir
 * kullanıcıyı anında üye yapıyordu. Gerçek davet akışında rol, kanonik rol ÖRNEĞİNİN
 * kimliğiyle seçiliyor: metinsel kademe hem özel rolleri ifade edemiyordu hem de
 * `[025]`'in kanonik model kararıyla çelişiyordu.
 */
export const inviteMemberSchema = z.object({
  // `emailSchema`: davet e-postası da kayıt/giriş ile AYNI biçimde normalize edilir
  // (trim + ASCII lowercase). Ham `z.email()` bıraksaydık `Ali@X.com` ile davet edilip
  // `ali@x.com` ile kabul eden akış arasında büyük/küçük harf uyuşmazlığı doğardı.
  email: emailSchema,
  roleId: z.uuid("Geçerli bir rol seçilmelidir"),
});
export type InviteMemberInput = z.infer<typeof inviteMemberSchema>;

/**
 * Kullanıcının KENDİ bekleyen davetleri — `GET /api/invitations/mine` (CLOS-224 `[047]`).
 *
 * Onboarding ekranı bunu gösteriyor. Token TAŞINMIYOR: token yalnız davet edilen kişinin
 * posta kutusunda bulunur (`[034]` kararı) ve listede dönseydi daveti gönderen kişi
 * başkasının davetini kabul edebilirdi.
 */
export const pendingInvitationSchema = z.object({
  id: z.uuid(),
  /** Davet eden organizasyonun adı — kullanıcının kararı için gereken tek bağlam. */
  organizationName: z.string(),
  organizationType: z.enum(ENTERPRISE_TENANT_TYPES),
  roleName: z.string(),
  expiresAt: z.string(),
});
export type PendingInvitation = z.infer<typeof pendingInvitationSchema>;
